import React, { useEffect, useState } from 'react';

import { FormikValues, useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminGetEntitlements,
  EntitlementStatusType,
  entitlementsSelector,
  IAdminEntitlementsParams,
  isLoadingSelector,
  successSelector,
  MiscType,
} from '@pharmaplan/common';

import DynamicTable from '../../DynamicTable';
import { IDynamicTableObject } from '../../DynamicTable/types';
import { Constants } from '../../../helpers/Constants';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import useTableSort from '../../../hooks/useTableSort';

import EntitlementsHeader from './EntitlementsHeader';
import { entitlementHeaders, entitlementRows } from './helper';
import useUserProfileLink from '../../../hooks/Admin/useUserProfileLink';
import useAdminPermissions from '../../../hooks/Admin/useAdminPermissions';

const loadingActions = [adminActions.getEntitlements];
const { editEntitlement } = Constants.paths.admin;

const Entitlements = () => {
  const { can } = useAdminPermissions();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const entitlements = useAppSelector(entitlementsSelector);
  const isSuccess = useAppSelector((state) =>
    successSelector([adminActions.submitPharmacyDetails], state));
  const isLoading = useAppSelector((state) =>
    isLoadingSelector(loadingActions, state));

  const { isDesc, orderBy, order, handleSorting, resetSorting } = useTableSort();
  const { goToUserProfile } = useUserProfileLink();

  const [page, setPage] = useState(1);

  const fetchEntitlements = (values: FormikValues, targetPage: number) => {
    const userType = values?.userType === MiscType.All ? '' : values.userType;
    const params: IAdminEntitlementsParams = {
      page: targetPage,
      userType,
      personnelId: values?.personnelId,
      status:
        values.status === '' ? '' : (values.status as EntitlementStatusType),
      sortBy: orderBy,
      desc: isDesc,
    };

    dispatch(adminGetEntitlements(params));
  };

  const handleSubmit = (values: FormikValues) => {
    setPage(1);
    fetchEntitlements(values, 1);
  };

  const formik = useFormik({
    initialValues: {
      personnelId: '',
      users: '',
      userSelected: '',
      userType: MiscType.All,
      pharmacyNameId: '',
      pharmacyEmailId: '',
      status: '',
    },
    onSubmit: handleSubmit,
  });

  const handleEdit = (entitlementId: string) =>
    () => {
      navigate(editEntitlement, { state: { entitlementId } });
    };

  const handleReset = () => {
    setPage(1);
    resetSorting();
    formik.resetForm();
    dispatch(adminGetEntitlements({ page: 1 }));
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const targetPage = selectedPage + 1;
    setPage(targetPage);
    fetchEntitlements(formik.values, targetPage);
  };

  const table: IDynamicTableObject = {
    headers: entitlementHeaders(),
    rows: entitlementRows({
      can,
      data: entitlements?.data,
      handleEdit,
      goToUserProfile,
    }),
    headerBar: [],
  };

  useEffect(() => {
    dispatch(adminGetEntitlements({ page: 1 }));
  }, [dispatch]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      fetchEntitlements(formik.values, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  useEffect(() => {
    if (isSuccess) {
      fetchEntitlements(formik.values, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <DynamicTable
      table={table}
      showHeader={false}
      loadSuccess={!isLoading}
      page={page}
      totalCount={entitlements?.totalCount}
      orderBy={orderBy}
      order={order}
      handleSort={handleSorting}
      handlePagination={handlePagination}
      customHeader={
        <EntitlementsHeader formik={formik} handleReset={handleReset} />
      }
    />
  );
};

export default Entitlements;
